// Overrides (spec/dashboard.md section 8.3): which parameters can be patched and by which OverrideData kind, the leaf
// paths of a template (A.B into inline objects), and the effective-value preview with its conflict detection — the
// engine's resolution rule (spec/liveops-engine.md section 11.1): the highest priority wins, ties go to the lowest
// unnyId compared as a number, then the lowest id.
import type { Document, Json, Parameter, Template } from '../../api/types';
import { isTempId } from '../../workspace/Workspace';
import type { ContentCatalog } from '../ContentContext';
import type { DocumentLike } from '../format';

/** A document by id: a draft or a loaded row at once, else fetched through the resolver (a picker may name a document the page has not loaded). */
export async function resolveDocument(catalog: ContentCatalog, id: string): Promise<Document | undefined> {
  const found = catalog.resolve(id);
  if (found) return found as Document;
  if (!isTempId(id)) await catalog.resolver.load([id]);
  return (catalog.resolve(id) ?? catalog.resolver.get(id)) as Document | undefined;
}

export const OVERRIDE_INFO = 'SmartObjects.OverrideInfo';
export const OVERRIDE_DATA = 'SmartObjects.OverrideData';
export const AB_TEST = 'SmartObjects.Analytics.ABTest';
export const AB_VARIANT = 'SmartObjects.Analytics.ABTestVariant';
export const AB_CONDITION = 'SmartObjects.Conditions.ABTestCondition';

/** The OverrideData template a parameter is patched with, or null when the engine has no patch kind for it (8.3). */
export function overrideTemplateFor(p: Pick<Parameter, 'type' | 'of'>): string | null {
  switch (p.type) {
    case 'int':
      return `${OVERRIDE_DATA}Int`;
    case 'long':
      return `${OVERRIDE_DATA}Long`;
    case 'float':
      return `${OVERRIDE_DATA}Float`;
    case 'bool':
      return `${OVERRIDE_DATA}Bool`;
    case 'string':
      return `${OVERRIDE_DATA}String`;
    case 'localized_string':
      return `${OVERRIDE_DATA}LocalizedStringNew`;
    case 'enum':
      return `${OVERRIDE_DATA}Enum`;
    case 'date_time':
      return `${OVERRIDE_DATA}DateTime`;
    case 'duration':
      return `${OVERRIDE_DATA}Duration`;
    case 'color':
      return `${OVERRIDE_DATA}Color`;
    case 'document':
      return `${OVERRIDE_DATA}Document`;
    case 'asset':
      return `${OVERRIDE_DATA}Asset`;
    case 'product':
      return `${OVERRIDE_DATA}Product`;
    case 'list':
      switch (p.of?.type) {
        case 'document':
          return `${OVERRIDE_DATA}ListDocument`;
        case 'int':
        case 'enum':
          return `${OVERRIDE_DATA}ListInt`;
        case 'float':
          return `${OVERRIDE_DATA}ListFloat`;
        case 'string':
          return `${OVERRIDE_DATA}ListString`;
        default:
          return null;
      }
    default:
      return null;
  }
}

/** The legacy twin of a kind reads as the modern one. */
export function normalizeOverrideTemplate(name: string): string {
  if (name === `${OVERRIDE_DATA}Image`) return `${OVERRIDE_DATA}Asset`;
  if (name === `${OVERRIDE_DATA}LocalizedString`) return `${OVERRIDE_DATA}LocalizedStringNew`;
  return name;
}

export interface OverridableLeaf {
  /** the path written into the patch: a parameter name, or A.B into an inline object */
  path: string;
  /** the leaf parameter (its type decides the widget and the kind) */
  param: Parameter;
  /** the words shown in the picker: `Price › Ads` */
  label: string;
  template: string;
}

const MAX_DEPTH = 3;

/** Every patchable leaf of a template: its own parameters and the fields of its inline objects, A.B deep. */
export function overridableLeaves(catalog: ContentCatalog, template: Template, prefix = '', depth = 0): OverridableLeaf[] {
  const out: OverridableLeaf[] = [];
  for (const p of template.effectiveParameters) {
    const path = prefix ? `${prefix}.${p.name}` : p.name;
    const label = prefix ? `${prefix.split('.').map(pretty).join(' › ')} › ${p.displayName ?? p.name}` : (p.displayName ?? p.name);
    const kind = overrideTemplateFor(p);
    if (kind && catalog.templates.has(kind)) out.push({ path, param: p, label, template: kind });
    else if (p.type === 'object' && p.template && depth < MAX_DEPTH) {
      const inline = catalog.templates.get(p.template);
      if (inline) out.push(...overridableLeaves(catalog, inline, path, depth + 1));
    }
  }
  return out;
}

function pretty(segment: string): string {
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

/** The value at a path (A.B into embedded objects) of a document's data. */
export function valueAt(data: Json | undefined, path: string): Json | undefined {
  let cur: Json | undefined = data;
  for (const seg of path.split('.')) {
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) return undefined;
    const obj = cur as Record<string, Json>;
    const key = Object.keys(obj).find((k) => k.toLowerCase() === seg.toLowerCase());
    cur = key === undefined ? undefined : obj[key];
  }
  return cur;
}

/** The leaf parameter a path names on a template, or undefined. */
export function paramAt(catalog: ContentCatalog, template: Template | undefined, path: string): Parameter | undefined {
  let t = template;
  const segs = path.split('.');
  for (let i = 0; i < segs.length; i++) {
    if (!t) return undefined;
    const p = t.effectiveParameters.find((x) => x.name.toLowerCase() === segs[i]!.toLowerCase());
    if (!p) return undefined;
    if (i === segs.length - 1) return p;
    t = p.type === 'object' && p.template ? catalog.templates.get(p.template) : undefined;
  }
  return undefined;
}

export interface OverrideGroupData {
  condition: string | null;
  overrides: string[];
  comments: string;
  priority: number;
}

export function groupsOf(doc: Pick<Document, 'data'>): OverrideGroupData[] {
  const raw = (doc.data as Record<string, Json> | null)?.groups;
  if (!Array.isArray(raw)) return [];
  return raw.map((g) => {
    const o = (g && typeof g === 'object' && !Array.isArray(g) ? g : {}) as Record<string, Json>;
    return {
      condition: typeof o.condition === 'string' ? o.condition : null,
      overrides: Array.isArray(o.overrides) ? o.overrides.filter((x): x is string => typeof x === 'string') : [],
      comments: typeof o.comments === 'string' ? o.comments : '',
      priority: typeof o.priority === 'number' ? o.priority : 100,
    };
  });
}

export function toGroupJson(g: OverrideGroupData): Json {
  return { condition: g.condition, overrides: g.overrides, comments: g.comments, priority: g.priority } as unknown as Json;
}

export interface PatchRow {
  /** the OverrideData* component */
  id: string;
  template: string;
  document: string | null;
  path: string;
  value: Json | undefined;
}

export function patchOf(node: DocumentLike | undefined, id: string): PatchRow | null {
  if (!node) return null;
  const d = (node.data ?? {}) as Record<string, Json>;
  return { id, template: normalizeOverrideTemplate(node.template), document: typeof d.document === 'string' ? d.document : null, path: typeof d.path === 'string' ? d.path : '', value: d.value };
}

export interface Candidate {
  override: Document;
  groupIndex: number;
  priority: number;
  row: PatchRow;
}

export interface Effective {
  document: string;
  path: string;
  winner: Candidate;
  others: Candidate[];
}

/** The number the engine compares unnyIds by (a document without one sorts after every numbered one, by id). */
export function unnyIdNumber(doc: Pick<Document, 'unnyId'>): number {
  const n = doc.unnyId !== null && doc.unnyId !== undefined ? Number(doc.unnyId) : NaN;
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function compareCandidates(a: Candidate, b: Candidate): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  const na = unnyIdNumber(a.override);
  const nb = unnyIdNumber(b.override);
  if (na !== nb) return na - nb;
  return a.override.id.localeCompare(b.override.id) || a.groupIndex - b.groupIndex;
}

/**
 * The effective value per (document, path) across the given override documents (the non-A/B family): the winner and the
 * losing candidates, so a card can render "field: default → winner" and flag conflicts (8.3).
 */
export function effectivePatches(catalog: ContentCatalog, overrides: Document[]): Effective[] {
  const byPair = new Map<string, Candidate[]>();
  for (const override of overrides) {
    groupsOf(override).forEach((g, groupIndex) => {
      for (const id of g.overrides) {
        const row = patchOf(catalog.resolve(id), id);
        if (!row || !row.document || !row.path) continue;
        const key = `${row.document}\u0000${row.path.toLowerCase()}`;
        const list = byPair.get(key) ?? [];
        list.push({ override, groupIndex, priority: g.priority, row });
        byPair.set(key, list);
      }
    });
  }
  const out: Effective[] = [];
  for (const [key, list] of byPair) {
    list.sort(compareCandidates);
    const [document, path] = key.split('\u0000') as [string, string];
    out.push({ document, path, winner: list[0]!, others: list.slice(1) });
  }
  out.sort((a, b) => a.document.localeCompare(b.document) || a.path.localeCompare(b.path));
  return out;
}

/** Whether a condition tree (by root id) contains an ABTestCondition naming the test. */
export function namesTest(catalog: ContentCatalog, rootId: string | null, testId: string, seen = new Set<string>()): string | null {
  if (!rootId || seen.has(rootId)) return null;
  seen.add(rootId);
  const node = catalog.resolve(rootId);
  if (!node) return null;
  const d = (node.data ?? {}) as Record<string, Json>;
  if (node.template === AB_CONDITION) return d.test === testId ? (typeof d.variant === 'string' ? d.variant : '') : null;
  const children: string[] = [];
  if (Array.isArray(d.conditions)) children.push(...d.conditions.filter((c): c is string => typeof c === 'string'));
  if (typeof d.condition === 'string') children.push(d.condition);
  for (const c of children) {
    const hit = namesTest(catalog, c, testId, seen);
    if (hit !== null) return hit;
  }
  return null;
}
